/* Repository path: frontend/src/features/reminders/ReminderOffPage.tsx */

import { useEffect, useRef, useState } from "react";
import { ApiError } from "../../api/client";
import { consumeFragmentToken, stopRemindersWithLink } from "../../api/planner";
import { Button } from "../../components/Button";

const INVALID_TOKEN_CODES = new Set(["stop_link_invalid", "token_invalid", "token_expired"]);

/**
 * The stop link every reminder email carries. Reads and strips the
 * fragment token exactly as `VerifyPage` does, but — unlike `VerifyPage` —
 * never submits on load: turning off every reminder for a plan is
 * consequential enough that a link scanner or a prefetch that merely opens
 * this page in the background must never trigger it. The token is
 * submitted only when the person presses the button below. See
 * docs/architecture/features/W5-02.md §5 and
 * docs/design/features/W5-02.md, "Stop page".
 */
export function ReminderOffPage({ navigate }: { navigate: (path: string, replace?: boolean) => void }) {
  const inFlight = useRef(false);
  const [token] = useState(() => consumeFragmentToken());
  const [state, setState] = useState<"ask" | "busy" | "success" | "already_used" | "invalid" | "transient">(
    () => (token ? "ask" : "invalid"),
  );
  const [retryMessage, setRetryMessage] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Moves focus to this page's own heading on load and on every state
  // change, exactly as `PlanItemDetail`'s own heading-focus discipline
  // does — never to the top of the page. See
  // docs/design/features/W5-02.md "Responsive/accessibility".
  useEffect(() => {
    headingRef.current?.focus();
  }, [state]);

  const submit = async () => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    setState("busy");
    setRetryMessage("");
    try {
      const result = await stopRemindersWithLink(token);
      inFlight.current = false;
      setState(result.status === "already_used" ? "already_used" : "success");
    } catch (error) {
      inFlight.current = false;
      const apiError = error instanceof ApiError ? error : null;
      if (apiError && (apiError.status === 410 || INVALID_TOKEN_CODES.has(apiError.code))) {
        setState("invalid");
        return;
      }
      setState("transient");
      setRetryMessage(apiError?.message ?? "We couldn’t turn reminders off right now. Try again.");
    }
  };

  if (state === "ask" || state === "busy") {
    return (
      <section className="boundary-card page--narrow">
        <p className="eyebrow">Email reminders</p>
        <h1 ref={headingRef} tabIndex={-1}>Stop reminder emails for this plan?</h1>
        <p>
          This turns off every reminder email for this saved plan, including reminders about
          single items. Your plan stays saved, and you can turn reminders back on from it at any
          time.
        </p>
        <Button onClick={() => void submit()} busy={state === "busy"}>
          {state === "busy" ? "Saving…" : "Turn off reminder emails"}
        </Button>
      </section>
    );
  }

  if (state === "success") {
    return (
      <section className="boundary-card page--narrow" role="status">
        <p className="eyebrow">Email reminders</p>
        <h1 ref={headingRef} tabIndex={-1}>Reminder emails are off for this plan</h1>
        <p>
          You won’t get any more reminder emails for it. To turn them back on, open your saved
          plan from a secure link.
        </p>
        <Button variant="secondary" onClick={() => navigate("/access/recovery")}>
          Request a secure link to your plan
        </Button>
      </section>
    );
  }

  if (state === "already_used") {
    return (
      <section className="boundary-card page--narrow" role="status">
        <p className="eyebrow">Email reminders</p>
        <h1 ref={headingRef} tabIndex={-1}>This link has already been used</h1>
        <p>
          It turned off reminder emails for this plan. If you’ve turned them back on since, you
          can turn them off again from your saved plan.
        </p>
        <Button variant="secondary" onClick={() => navigate("/access/recovery")}>
          Request a secure link to your plan
        </Button>
      </section>
    );
  }

  if (state === "transient") {
    return (
      <section className="boundary-card page--narrow" role="alert">
        <p className="eyebrow">Email reminders</p>
        <h1 ref={headingRef} tabIndex={-1}>We couldn’t turn reminders off yet</h1>
        <p>{retryMessage}</p>
        <Button onClick={() => void submit()}>Try again</Button>
      </section>
    );
  }

  return (
    <section className="boundary-card page--narrow" role="alert">
      <p className="eyebrow">Email reminders</p>
      <h1 ref={headingRef} tabIndex={-1}>This link has expired or is no longer valid</h1>
      <p>
        You can still turn reminders off from your saved plan. For privacy, we can’t say whether
        a saved plan exists for this link.
      </p>
      <Button variant="secondary" onClick={() => navigate("/access/recovery")}>
        Request a secure link to your plan
      </Button>
    </section>
  );
}
